import { useState } from "react";
import { useParams } from "react-router-dom";
import { Flex, Form, Typography } from "antd";
import { useNotification, useTranslate } from "@refinedev/core";
import { PlusIcon } from "@phosphor-icons/react";
import { Skeleton } from "@libertify/ui";
import {
  useQuiz,
  useSaveQuiz,
  type QuizQuestion,
} from "@libertify/experience-viewer";
import { API_URL } from "@/constants";
import { useAuth } from "hooks/useAuth";
import { PlaybookAddOnCard } from "../../PlaybookAddOnCard";
import { DashedAddButton } from "../../DashedAddButton";
import { CONFIG_ITEM } from "../../../data/addons";
import {
  QUIZ_ANSWERS_PER_QUESTION,
  type QuizQuestionRow,
  type QuizQuestionsFormValues,
} from "../types";
import { useAssetListAutosave } from "../hooks/useAssetListAutosave";
import { QuizQuestionItem } from "./QuizQuestionItem";

const KEY = "experienceEdit.quizCard";

/** Session-only row ids: unique for the life of the page is all they need. */
let nextRowId = 0;
const newRowId = (): string => `question-${++nextRowId}`;

const blankAnswers = (): { text: string }[] =>
  Array.from({ length: QUIZ_ANSWERS_PER_QUESTION }, () => ({ text: "" }));

/** A fresh, empty question — what "Add a question" appends. */
const newQuestionRow = (): QuizQuestionRow => ({
  id: newRowId(),
  question: "",
  answers: blankAnswers(),
  correctIndex: 0,
});

/**
 * A wire question as the editor holds it. The four options are padded out when
 * the asset carries fewer, so the row always has the fixed set of fields the
 * item renders; `is_correct` collapses to the single `correctIndex` a radio
 * group needs.
 *
 * Four options, exactly one of them correct, is the format — so the key is
 * forced back into that range rather than trusted. An asset marking none, or
 * marking one outside the four rendered options, would otherwise show no
 * selected radio and then save every option as wrong on the next keystroke,
 * leaving a question with no correct answer at all.
 */
const toRow = (question: QuizQuestion): QuizQuestionRow => {
  const answers = question.answers ?? [];
  const marked = answers.findIndex((answer) => answer.is_correct);
  const isRenderable = marked >= 0 && marked < QUIZ_ANSWERS_PER_QUESTION;
  return {
    id: newRowId(),
    question: question.question,
    answers: Array.from({ length: QUIZ_ANSWERS_PER_QUESTION }, (_, index) => ({
      text: answers.at(index)?.text ?? "",
    })),
    correctIndex: isRenderable ? marked : 0,
    // Only carried when the answer they describe is one of the four on screen.
    // Forcing the key back into range already changes which answer is correct,
    // so keeping the citations would attach evidence for the old one to the
    // new one — worse than having none.
    ...(isRenderable
      ? { sources: question.sources, sourcedAnswer: answers.at(marked)?.text }
      : {}),
  };
};

/**
 * An existing question as it should be sent while it is being edited: every
 * field cleared to be retyped keeps the text the backend last accepted, so a
 * blank option never ships to viewers mid-edit. A field the backend already
 * held blank (a generated bank can arrive that way) stays blank, so the
 * question remains saveable.
 */
const fillFromSaved = (
  row: Partial<QuizQuestionRow>,
  saved: QuizQuestionRow
): QuizQuestionRow => ({
  ...saved,
  ...row,
  question: row.question?.trim() ? row.question : saved.question,
  answers: Array.from({ length: QUIZ_ANSWERS_PER_QUESTION }, (_, index) => {
    const text = row.answers?.at(index)?.text;
    return {
      text: text?.trim() ? text : (saved.answers.at(index)?.text ?? ""),
    };
  }),
});

/**
 * Every field carries something. The bar a BRAND-NEW question has to clear
 * before it is sent: a blank option reaches viewers as an unanswerable choice,
 * which is worse than the question simply not being there yet.
 *
 * Existing questions go through `fillFromSaved` instead: holding their saves
 * until every field is filled would leave a generated bank with a blank
 * option permanently unsaveable.
 */
const isCompleteQuestion = (row: Partial<QuizQuestionRow>): boolean =>
  Boolean(row.question?.trim()) &&
  Array.from({ length: QUIZ_ANSWERS_PER_QUESTION }, (_, index) =>
    row.answers?.at(index)?.text?.trim()
  ).every(Boolean);

/**
 * The rows as the bank is persisted: the wire shape, ids and `correctIndex`
 * folded back into per-answer `is_correct`.
 *
 * `sources` rides through untouched while it still describes the answer that
 * is marked correct — the backend fills it at generation and the editor has no
 * UI for it, so dropping it on every save would strip the citations. Once the
 * correct answer moves or its text is rewritten they no longer support it, and
 * keeping them would leave the asset citing the wrong answer.
 */
const toBank = (rows: Partial<QuizQuestionRow>[]): QuizQuestion[] =>
  rows.map((row) => {
    const correctIndex = row.correctIndex ?? 0;
    const answers = Array.from(
      { length: QUIZ_ANSWERS_PER_QUESTION },
      (_, index) => ({
        text: row.answers?.at(index)?.text ?? "",
        is_correct: index === correctIndex,
      })
    );
    const stillSourced =
      row.sources !== undefined &&
      answers.at(correctIndex)?.text === row.sourcedAnswer;
    return {
      question: row.question ?? "",
      answers,
      ...(stillSourced ? { sources: row.sources } : {}),
    };
  });

const sameQuestion = (a: QuizQuestion, b: QuizQuestion): boolean =>
  a.question === b.question &&
  a.answers.length === b.answers.length &&
  a.answers.every((answer, i) => {
    const other = b.answers.at(i);
    return (
      answer.text === other?.text &&
      answer.is_correct === other?.is_correct
    );
  });

const sameBank = (a: QuizQuestion[], b: QuizQuestion[]): boolean =>
  a.length === b.length &&
  a.every((question, i) => {
    const other = b.at(i);
    return other !== undefined && sameQuestion(question, other);
  });

export interface QuizEditCardProps {
  /** Whether the quiz is shown to viewers of the experience. */
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visiblePending?: boolean;
}

/**
 * Quiz card for the experience edit page: the generated question bank as one
 * editable question per row — its text, four answer options and which of them
 * is correct — each removable, plus "Add a question".
 *
 * Saves like the Quick recall and Podcast edit cards: every change, debounced,
 * nothing to press. The bank lives in its own asset and there is no
 * per-question endpoint, so each save replaces the whole thing (see
 * `useSaveQuiz`). A saved bank is not what viewers see: the publication
 * snapshots its assets, and the backend marks it out of date on every asset
 * PATCH. The user republishes ("Update" in the out-of-date widget) when the
 * quiz is ready.
 */
export const QuizEditCard = ({
  visible,
  onVisibleChange,
  visiblePending,
}: QuizEditCardProps) => {
  const t = useTranslate();
  const { projectId } = useParams<{ projectId: string }>();
  // Same bearer the experience page hands the viewer's add-on reads.
  const { accessToken } = useAuth();
  const { questions, assetId, isLoading, isError } = useQuiz({
    apiBaseUrl: API_URL,
    projectId: projectId ?? "",
    token: accessToken,
  });

  const renderBody = () => {
    if (isLoading) return <Skeleton variant="text" rows={6} />;
    if (isError) {
      return (
        <Typography.Text type="danger">{t(`${KEY}.error`)}</Typography.Text>
      );
    }
    // No asset yet means generation hasn't produced the quiz — there is
    // nothing to write into, so no editor either.
    if (!assetId) {
      return (
        <Typography.Text type="secondary">
          {t(`${KEY}.notGenerated`)}
        </Typography.Text>
      );
    }
    // Keyed on the asset: a regenerated quiz is a new asset with a new bank,
    // and a form seeded from the old one would write its rows over it.
    return (
      <QuizQuestionsForm
        key={assetId}
        loaded={questions}
        assetId={assetId}
        projectId={projectId ?? ""}
        accessToken={accessToken}
      />
    );
  };

  return (
    <PlaybookAddOnCard
      itemId={CONFIG_ITEM.Quiz}
      title={t(`${KEY}.title`)}
      description={t(`${KEY}.subtitle`)}
      visible={visible}
      visiblePending={visiblePending}
      onVisibleChange={onVisibleChange}
    >
      {renderBody()}
    </PlaybookAddOnCard>
  );
};

interface QuizQuestionsFormProps {
  loaded: QuizQuestion[];
  assetId: string;
  projectId: string;
  accessToken: string | null | undefined;
}

/**
 * Mounted once the bank is in: antd reads `initialValues` only at mount. Owns
 * the autosave — debounced, flushed on unmount (the card unmounts on every
 * rail switch), reverted on failure — mirroring `QuickRecallEditCard`.
 *
 * Rows carry a session id (see `QuizQuestionRow`) so the save can tell a
 * freshly added blank row, which is simply left out, from an existing question
 * whose text was cleared to be retyped, which holds the save instead of being
 * deleted.
 */
const QuizQuestionsForm = ({
  loaded,
  assetId,
  projectId,
  accessToken,
}: QuizQuestionsFormProps) => {
  const t = useTranslate();
  const { open } = useNotification();
  const [form] = Form.useForm<QuizQuestionsFormValues>();
  const { save } = useSaveQuiz({
    apiBaseUrl: API_URL,
    projectId,
    token: accessToken,
  });

  // Ids are minted once, at mount — the loaded bank may be re-read later, and
  // the form owns the rows from here on.
  const [initialRows] = useState<QuizQuestionRow[]>(() => loaded.map(toRow));

  const persist = useAssetListAutosave<QuizQuestionRow, QuizQuestion>({
    initialRows,
    saveKey: assetId,
    // A field of an existing question cleared to be retyped is an edit in
    // progress, not content: it keeps its last saved text until text comes
    // back, so a blank option never reaches viewers and a fully cleared
    // question isn't deleted (leaving it out is what would delete it). The
    // fields and questions around it save normally meanwhile. A question that
    // was never saved waits until every field carries something.
    resolveRow: (row, previous) => {
      if (previous) return fillFromSaved(row, previous);
      return isCompleteQuestion(row) ? (row as QuizQuestionRow) : null;
    },
    toWire: toBank,
    isSameWire: sameBank,
    save: (questions) => save(assetId, questions),
    onSaveFailed: () =>
      open?.({ type: "error", message: t(`${KEY}.saveFailed`) }),
    revert: (questions) => form.setFieldsValue({ questions }),
  });

  return (
    <Form<QuizQuestionsFormValues>
      form={form}
      layout="vertical"
      initialValues={{ questions: initialRows }}
      onValuesChange={(_, values) => persist(values.questions ?? [])}
    >
      <Form.List name="questions">
        {(fields, { add, remove }) => (
          <Flex vertical gap={16}>
            {fields.length === 0 ? (
              <Typography.Text type="secondary">
                {t(`${KEY}.empty`)}
              </Typography.Text>
            ) : null}
            {fields.map((field, index) => (
              <QuizQuestionItem
                key={field.key}
                name={field.name}
                position={index + 1}
                onRemove={() => remove(field.name)}
              />
            ))}
            <DashedAddButton
              icon={<PlusIcon size={16} weight="bold" />}
              label={t(`${KEY}.add`)}
              onClick={() => add(newQuestionRow())}
              iconBordered={false}
              centered
            />
          </Flex>
        )}
      </Form.List>
    </Form>
  );
};

export default QuizEditCard;
